import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Enums } from "@how/database";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type OverrideReason = Enums<"check_in_override_reason">;
export const OVERRIDE_LABEL: Record<OverrideReason, string> = {
  tag_lost: "The tag is lost",
  known_guardian: "A guardian the church knows",
  other: "Another reason",
};

export function formatWhen(iso: string, timeZone?: string | null): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

export interface DoorOccurrence {
  readonly occurrenceId: string;
  readonly eventTitle: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly campusName: string | null;
  readonly campusTimeZone: string | null;
  readonly openSessionId: string | null;
}

/** The dates a door can open: from six hours ago to seven days ahead, with any session already open on them. */
export function useDoorOccurrences() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["door-occurrences", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<DoorOccurrence[]> => {
      const from = new Date(Date.now() - 6 * 3600_000).toISOString();
      const to = new Date(Date.now() + 7 * 24 * 3600_000).toISOString();
      const { data, error } = await supabase
        .from("event_occurrences")
        .select(
          "id, starts_at, ends_at, is_cancelled, events!inner(title, is_active, campuses(name, time_zone)), check_in_sessions(id, closed_at)",
        )
        .eq("tenant_id", tenantId ?? "")
        .eq("is_cancelled", false)
        .gte("starts_at", from)
        .lte("starts_at", to)
        .order("starts_at");
      if (error) throw error;
      return data
        .filter((o) => o.events.is_active)
        .map((o) => ({
          occurrenceId: o.id,
          eventTitle: o.events.title,
          startsAt: o.starts_at,
          endsAt: o.ends_at,
          campusName: o.events.campuses?.name ?? null,
          campusTimeZone: o.events.campuses?.time_zone ?? null,
          openSessionId: o.check_in_sessions.find((s) => s.closed_at === null)?.id ?? null,
        }));
    },
  });
}

export interface Room {
  readonly id: string;
  readonly name: string;
  readonly ageMin: number | null;
  readonly ageMax: number | null;
  readonly capacity: number | null;
}

export function useRooms() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["check-in-rooms", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<Room[]> => {
      const { data, error } = await supabase
        .from("check_in_locations")
        .select("id, name, age_min, age_max, capacity")
        .eq("tenant_id", tenantId ?? "")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((r) => ({
        id: r.id,
        name: r.name,
        ageMin: r.age_min,
        ageMax: r.age_max,
        capacity: r.capacity,
      }));
    },
  });
}

const rosterSchema = z.object({
  session: z.object({
    id: z.string(),
    openedAt: z.string(),
    closedAt: z.string().nullable(),
    occurrenceId: z.string(),
    event: z.string(),
    startsAt: z.string(),
  }),
  checkIns: z.array(
    z.object({
      checkInId: z.string(),
      personId: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      isChild: z.boolean(),
      room: z.string().nullable(),
      locationId: z.string().nullable(),
      labelCode: z.string(),
      securityCode: z.string(),
      checkedInAt: z.string(),
      checkedOutAt: z.string().nullable(),
      collectedBy: z.string().nullable(),
      household: z.string().nullable(),
      /** For a child: whether the collector was on the list; null before check-out and for adults (#174). */
      collectorListed: z.boolean().nullable(),
      /** For a child: who may collect them (household adults, then the people the church named). */
      collectors: z
        .array(
          z.object({
            name: z.string(),
            relationship: z.string().nullable(),
            source: z.enum(["household", "named"]),
          }),
        )
        .nullable(),
    }),
  ),
});
export type SessionRoster = z.infer<typeof rosterSchema>;
export type RosterRow = SessionRoster["checkIns"][number];

/** Who is in the room now and who has gone home. Polled, since several doors may share a session. */
export function useSessionRoster(sessionId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["session-roster", tenantId, sessionId],
    enabled: sessionId !== null,
    refetchInterval: 15_000,
    queryFn: async (): Promise<SessionRoster> => {
      const { data, error } = await supabase.rpc("session_roster", {
        p_session_id: sessionId ?? "",
      });
      if (error) throw error;
      return rosterSchema.parse(data);
    },
  });
}

const householdSchema = z.object({
  householdId: z.string(),
  name: z.string(),
  members: z.array(
    z.object({
      personId: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      role: z.enum(["adult", "child", "other"]),
      isChild: z.boolean(),
      checkedIn: z.boolean(),
      suggestedLocationId: z.string().nullable(),
    }),
  ),
});
export type FoundHousehold = z.infer<typeof householdSchema>;
export type FoundMember = FoundHousehold["members"][number];

export function useFindHousehold(sessionId: string | null, search: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const trimmed = search.trim();
  return useQuery({
    queryKey: ["find-household", tenantId, sessionId, trimmed],
    enabled: sessionId !== null && trimmed.length >= 2,
    queryFn: async (): Promise<FoundHousehold[]> => {
      const { data, error } = await supabase.rpc("find_household_for_check_in", {
        p_session_id: sessionId ?? "",
        p_search: trimmed,
      });
      if (error) throw error;
      return z.array(householdSchema).parse(data);
    },
  });
}

const labelSchema = z.object({
  checkInId: z.string(),
  labelCode: z.string(),
  securityCode: z.string(),
  isChild: z.boolean(),
  firstName: z.string(),
  lastInitial: z.string(),
  room: z.string().nullable(),
  event: z.string(),
  startsAt: z.string(),
});
export type Label = z.infer<typeof labelSchema>;

function useStationInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (sessionId?: string) => {
    await client.invalidateQueries({ queryKey: ["door-occurrences", tenantId] });
    if (sessionId !== undefined) {
      await client.invalidateQueries({ queryKey: ["session-roster", tenantId, sessionId] });
      await client.invalidateQueries({ queryKey: ["find-household", tenantId, sessionId] });
    }
  };
}

export function useOpenSession() {
  const supabase = useSupabase();
  const invalidate = useStationInvalidation();
  return useMutation({
    mutationFn: async (occurrenceId: string) => {
      const { data, error } = await supabase.rpc("open_check_in_session", {
        p_occurrence_id: occurrenceId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (id) => invalidate(id),
  });
}

export function useCloseSession() {
  const supabase = useSupabase();
  const invalidate = useStationInvalidation();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase.rpc("close_check_in_session", { p_session_id: sessionId });
      if (error) throw error;
    },
    onSuccess: (_r, sessionId) => invalidate(sessionId),
  });
}

/** Check several people in at once (a family at the door); returns a label per person. */
export function useCheckIn() {
  const supabase = useSupabase();
  const invalidate = useStationInvalidation();
  return useMutation({
    mutationFn: async (input: {
      sessionId: string;
      people: readonly { personId: string; locationId: string | null }[];
    }) => {
      const labels: Label[] = [];
      for (const p of input.people) {
        const { data, error } = await supabase.rpc("check_in_person", {
          p_session_id: input.sessionId,
          p_person_id: p.personId,
          ...(p.locationId === null ? {} : { p_location_id: p.locationId }),
        });
        if (error) throw error;
        labels.push(labelSchema.parse(data));
      }
      return labels;
    },
    onSuccess: (_labels, input) => invalidate(input.sessionId),
  });
}

export function useCheckOut() {
  const supabase = useSupabase();
  const invalidate = useStationInvalidation();
  return useMutation({
    mutationFn: async (input: {
      sessionId: string;
      checkInId: string;
      collectedBy: string;
      securityCode: string;
      overrideReason: OverrideReason | null;
    }) => {
      const { error } = await supabase.rpc("check_out_person", {
        p_check_in_id: input.checkInId,
        ...(input.collectedBy.trim() === "" ? {} : { p_collected_by_name: input.collectedBy }),
        ...(input.securityCode.trim() === "" ? {} : { p_security_code: input.securityCode }),
        ...(input.overrideReason === null ? {} : { p_override_reason: input.overrideReason }),
      });
      if (error) throw error;
    },
    onSuccess: (_r, input) => invalidate(input.sessionId),
  });
}

/** The room the station suggests for a person, by the rooms' age ranges; adults get none. */
/**
 * The room a child starts in: the one the database suggested from the child's age (the age itself never reaches
 * the station, D12), else the first room so the person at the door has something to change rather than to find.
 */
export function suggestRoom(rooms: readonly Room[], member: FoundMember): string | null {
  if (!member.isChild) return null;
  return member.suggestedLocationId ?? rooms[0]?.id ?? null;
}
